import { SupplementaryInsuranceTypes } from '../../../states/offer/offer.state';
import { Item } from './tracking-ecommerce.events';
import {
  CATEGORY,
  genderNames,
  mapBrand,
  mapContractLength,
  mapCustomerType,
  mapExistingCustomer,
} from './tracking-ecommerce.map';
import {
  BasicOfferType,
  Complementary,
  ComplementaryViva,
  ContractDuration,
  GenderOrPrenatal,
  HospitalOfferType,
  HospitalVivaOfferType,
  Insurer,
  Level,
  Outpatient,
  OutpatientViva,
} from '../../../api';
import { isNotNullOrUndefined } from '../../../utils/typeUtils';
import {
  HospitalOrBasicInsurance,
  SupplementaryInsurancesData,
  Youth,
} from '../../../../pages/supplementary-insurance/supplementary-insurance-selection-page/supplementary-insurance.mapper';

const supplementaryInsuranceIds: {
  [key in SupplementaryInsuranceTypes]: string;
} = {
  basic: 'basic',
  youth: 'youth',
  hospital: 'hospital',
  hospitalViva: 'hospital_viva',
  complementary: 'complementary',
  complementaryViva: 'complementary_viva',
  outpatient: 'outpatient',
  outpatientComfort: 'outpatient_comfort',
  outpatientViva: 'outpatient_viva',
  legalProtection: 'legal_protection',
  hospitalCapital: 'hospital_capital',
  accidentDeathCapital: 'accident_death_capital',
  accidentDisabilityCapital: 'accident_disability_capital',
  sicknessDeathOrDisabilityCapital: 'sickness_death_or_disability_capital',
  dental: 'dental',
  hospitalDailyCash: 'hospital_daily_cash',
  dailyCash: 'daily_cash',
  emergencyCare: 'emergency_care',
  accidentMedicalExpensesSupplement: 'accident_medical_expenses_supplement',
};

const supplementaryInsuranceNames: {
  [key in SupplementaryInsuranceTypes]: string;
} = {
  basic: 'Basic',
  youth: 'Jugendpaket',
  hospital: 'Spital',
  complementary: 'Komplementär',
  outpatient: 'Ambulant',
  outpatientComfort: 'Ambulant Comfort',
  legalProtection: 'Rechtsschutz',
  hospitalCapital: 'Spitalkapital',
  accidentDeathCapital: 'Todesfallkapital bei Unfall',
  accidentDisabilityCapital: 'Invaliditätskapital bei Unfall',
  sicknessDeathOrDisabilityCapital: 'Kapitalversicherung infolge Krankheit',
  dental: 'Zahnbehandlung',
  hospitalDailyCash: 'Spitaltaggeld',
  dailyCash: 'Taggeld',
  emergencyCare: 'Notfallbetreuung',
  accidentMedicalExpensesSupplement: 'Heilungskostenzusatz bei Unfall',
  hospitalViva: 'Spital VIVA',
  complementaryViva: 'Komplementär VIVA',
  outpatientViva: 'Ambulant VIVA',
};

const basicId: {
  [key in BasicOfferType]: string;
} = {
  BASIC_GENERAL: 'basic_general',
  BASIC_FLEX_24: 'basic_flex_24',
  BASIC_FLEX_48: 'basic_flex_48',
  BASIC_SEMI_PRIVATE: 'basic_semi_private',
  BASIC_PRIVATE: 'basic_private',
};

const outpatientId: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'outpatient_level_one',
  LEVEL_TWO: 'outpatient_level_two',
  LEVEL_THREE: 'outpatient_level_three',
};

const outpatientVivaId: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'outpatient_viva_level_one',
  LEVEL_TWO: 'outpatient_viva_level_two',
  LEVEL_THREE: 'outpatient_viva_level_three',
};

const complementaryId: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'complementary_level_one',
  LEVEL_TWO: 'complementary_level_two',
  LEVEL_THREE: 'complementary_level_three',
};

const complementaryVivaId: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'complementary_viva_level_one',
  LEVEL_TWO: 'complementary_viva_level_two',
  LEVEL_THREE: 'complementary_viva_level_three',
};

const youthId: {
  [key in Level]: string;
} = {
  LEVEL_ONE: 'youth_level_one',
  LEVEL_TWO: 'youth_level_two',
  LEVEL_THREE: 'youth_level_three',
};

const hospitalId: {
  [key in HospitalOfferType]: string;
} = {
  HOSPITAL_GENERAL: 'hospital_general',
  HOSPITAL_FLEX_PLUS: 'hospital_flex_plus',
  HOSPITAL_FLEX_48: 'hospital_flex_48',
  HOSPITAL_FLEX_24: 'hospital_flex_24',
  HOSPITAL_SEMI_PRIVATE: 'hospital_semi_private',
  HOSPITAL_PRIVATE: 'hospital_private',
};

const hospitalVivaId: {
  [key in HospitalVivaOfferType]: string;
} = {
  HOSPITAL_VIVA_GENERAL: 'hospital_viva_general',
  HOSPITAL_VIVA_FLEX_48: 'hospital_viva_flex_48',
  HOSPITAL_VIVA_FLEX_24: 'hospital_viva_flex_24',
  HOSPITAL_VIVA_SEMI_PRIVATE: 'hospital_viva_semi_private',
  HOSPITAL_VIVA_PRIVATE: 'hospital_viva_private',
};

export function mapSupplementaryInsuranceItem(
  product: SupplementaryInsuranceTypes,
  price: number,
  gender: GenderOrPrenatal,
): Item {
  return {
    item_id: supplementaryInsuranceIds[product],
    item_name: supplementaryInsuranceNames[product],
    item_category: CATEGORY.SUPPLEMENTARY_INSURANCE,
    item_category3: genderNames[gender],
    price,
    quantity: 1,
  };
}

export function mapSupplementaryInsuranceVariantItem(
  product: SupplementaryInsuranceTypes,
  price: number,
  gender: GenderOrPrenatal,
): Item {
  return mapItem(supplementaryInsuranceIds[product], supplementaryInsuranceNames[product], 1, price, gender);
}

export function mapComplementaryVariantItem(
  complementary: Complementary,
  index: number,
  gender: GenderOrPrenatal,
): Item {
  return mapItem(
    complementaryId[complementary.level],
    supplementaryInsuranceNames['complementary'],
    index,
    complementary.price,
    gender,
  );
}

export function mapComplementaryVivaVariantItem(
  complementary: ComplementaryViva,
  index: number,
  gender: GenderOrPrenatal,
): Item {
  return mapItem(
    complementaryVivaId[complementary.level],
    supplementaryInsuranceNames['complementaryViva'],
    index,
    complementary.price,
    gender,
  );
}

export function mapYouthVariantItem(youth: Youth, index: number, gender: GenderOrPrenatal): Item {
  return mapItem(youthId[youth.level], supplementaryInsuranceNames['youth'], index, youth.price, gender);
}

export function mapOutpatientVariantItem(outpatient: Outpatient, index: number, gender: GenderOrPrenatal): Item {
  return mapItem(
    outpatientId[outpatient.level],
    supplementaryInsuranceNames['outpatient'],
    index,
    outpatient.price,
    gender,
  );
}

export function mapOutpatientVivaVariantItem(
  outpatient: OutpatientViva,
  index: number,
  gender: GenderOrPrenatal,
): Item {
  return mapItem(
    outpatientVivaId[outpatient.level],
    supplementaryInsuranceNames['outpatientViva'],
    index,
    outpatient.price,
    gender,
  );
}

export function mapHospitalVariantItemOrUndefined(
  price: number | undefined,
  offerType: HospitalOfferType,
  gender: GenderOrPrenatal,
): Item | undefined {
  return isNotNullOrUndefined(price) && price !== Infinity
    ? mapItem(hospitalId[offerType], supplementaryInsuranceNames['hospital'], 0, price, gender)
    : undefined;
}

export function mapHospitalVivaVariantItemOrUndefined(
  price: number | undefined,
  offerType: HospitalVivaOfferType,
  gender: GenderOrPrenatal,
): Item | undefined {
  return isNotNullOrUndefined(price) && price !== Infinity
    ? mapItem(hospitalVivaId[offerType], supplementaryInsuranceNames['hospitalViva'], 0, price, gender)
    : undefined;
}

export function mapBasicVariantItemOrUndefined(
  price: number | undefined,
  offerType: BasicOfferType,
  gender: GenderOrPrenatal,
): Item | undefined {
  return isNotNullOrUndefined(price) && price !== Infinity
    ? mapItem(basicId[offerType], supplementaryInsuranceNames['basic'], 0, price, gender)
    : undefined;
}

function mapItem(itemId: string, itemName: string, index: number, price: number, gender: GenderOrPrenatal): Item {
  return {
    item_id: itemId,
    item_name: itemName,
    item_category: CATEGORY.SUPPLEMENTARY_INSURANCE,
    item_category3: genderNames[gender],
    price: price,
    quantity: 1,
  };
}

export function mapSupplementaryInsuranceItems(
  supplementaryInsuranceData: SupplementaryInsurancesData,
  gender: GenderOrPrenatal,
  visibleProducts: SupplementaryInsuranceTypes[],
): Item[] {
  const items = visibleProducts
    .map((insuranceType) =>
      mapSupplementaryInsuranceItemOrUndefined(
        insuranceType,
        supplementaryInsuranceData[`${insuranceType}LowestPrice`],
        gender,
      ),
    )
    .filter(isNotNullOrUndefined) as Item[];
  return items.map((item, index) => ({ ...item, index: index + 1 }));
}

function mapSupplementaryInsuranceItemOrUndefined(
  supplementaryInsuranceTypes: SupplementaryInsuranceTypes,
  price: number | undefined,
  gender: GenderOrPrenatal,
): Item | undefined {
  return isNotNullOrUndefined(price)
    ? mapSupplementaryInsuranceItem(supplementaryInsuranceTypes, price, gender)
    : undefined;
}

export function mapSupplementaryInsurancesOfProduct(
  product: SupplementaryInsuranceTypes,
  supplementaryInsurancesData: SupplementaryInsurancesData,
  gender: GenderOrPrenatal,
): Item[] {
  switch (product) {
    case 'basic':
      const basic = supplementaryInsurancesData['basic']!;
      return buildBasicItems(basic, gender);
    case 'hospital':
      const hospital = supplementaryInsurancesData['hospital']!;
      return buildHospitalItems(hospital, gender);
    case 'hospitalViva':
      const hospitalViva = supplementaryInsurancesData['hospitalViva']!;
      return buildHospitalVivaItems(hospitalViva, gender);
    case 'complementary':
      const complementary = supplementaryInsurancesData['complementary']!;
      return buildComplementaryItems(complementary, gender);
    case 'outpatient':
      const outpatient = supplementaryInsurancesData['outpatient']!;
      return buildOutpatientItems(outpatient, gender);
    case 'outpatientViva':
      const outpatientViva = supplementaryInsurancesData['outpatientViva']!;
      return buildOutpatientVivaItems(outpatientViva, gender);
    case 'complementaryViva':
      const complementaryViva = supplementaryInsurancesData['complementaryViva']!;
      return buildComplementaryVivaItems(complementaryViva, gender);
    case 'youth':
      const youth = supplementaryInsurancesData['youth']!;
      return buildYouthItems(youth, gender);
    // NOTE: All other supplementary insurances are mapped onto one entry
    default:
      const lowestPrice = supplementaryInsurancesData[`${product}LowestPrice`] || 0;
      return [mapSupplementaryInsuranceVariantItem(product, lowestPrice, gender)];
  }
}

function buildBasicItems(basicInsurance: HospitalOrBasicInsurance, gender: GenderOrPrenatal): Item[] {
  const items = [
    mapBasicVariantItemOrUndefined(basicInsurance.general?.price, 'BASIC_GENERAL', gender),
    mapBasicVariantItemOrUndefined(basicInsurance.flex.flex24?.price, 'BASIC_FLEX_24', gender),
    mapBasicVariantItemOrUndefined(basicInsurance.flex.flex48?.price, 'BASIC_FLEX_48', gender),
    mapBasicVariantItemOrUndefined(
      Math.min(...basicInsurance.semiPrivate.map((i) => i.price)),
      'BASIC_SEMI_PRIVATE',
      gender,
    ),
    mapBasicVariantItemOrUndefined(Math.min(...basicInsurance.private.map((i) => i.price)), 'BASIC_PRIVATE', gender),
  ];
  return items
    .filter((item) => isNotNullOrUndefined(item))
    .map((item, index) => ({ ...item, index: index + 1 })) as Item[];
}

function buildComplementaryItems(complementary: Complementary[], gender: GenderOrPrenatal): Item[] {
  return complementary.map((c, index) => mapComplementaryVariantItem(c, index + 1, gender));
}

function buildComplementaryVivaItems(complementaryViva: ComplementaryViva[], gender: GenderOrPrenatal): Item[] {
  return complementaryViva.map((c, index) => mapComplementaryVivaVariantItem(c, index + 1, gender));
}

function buildYouthItems(youth: Youth[], gender: GenderOrPrenatal): Item[] {
  return youth.map((y, index) => mapYouthVariantItem(y, index + 1, gender));
}

function buildOutpatientItems(outpatient: Outpatient[], gender: GenderOrPrenatal): Item[] {
  return outpatient.map((c, index) => mapOutpatientVariantItem(c, index + 1, gender));
}

function buildOutpatientVivaItems(outpatient: Outpatient[], gender: GenderOrPrenatal): Item[] {
  return outpatient.map((c, index) => mapOutpatientVivaVariantItem(c, index + 1, gender));
}

function buildHospitalItems(hospitalInsurance: HospitalOrBasicInsurance, gender: GenderOrPrenatal): Item[] {
  const items = [
    mapHospitalVariantItemOrUndefined(hospitalInsurance.general?.price, 'HOSPITAL_GENERAL', gender),
    mapHospitalVariantItemOrUndefined(hospitalInsurance.flex.flex24?.price, 'HOSPITAL_FLEX_24', gender),
    mapHospitalVariantItemOrUndefined(hospitalInsurance.flex.flex48?.price, 'HOSPITAL_FLEX_48', gender),
    mapHospitalVariantItemOrUndefined(hospitalInsurance.flex.flex48?.price, 'HOSPITAL_FLEX_PLUS', gender),
    mapHospitalVariantItemOrUndefined(
      Math.min(...hospitalInsurance.semiPrivate.map((i) => i.price)),
      'HOSPITAL_SEMI_PRIVATE',
      gender,
    ),
    mapHospitalVariantItemOrUndefined(
      Math.min(...hospitalInsurance.private.map((i) => i.price)),
      'HOSPITAL_PRIVATE',
      gender,
    ),
  ];
  return items.filter((i) => isNotNullOrUndefined(i)).map((i, index) => ({ ...i, index: index + 1 })) as Item[];
}

function buildHospitalVivaItems(hospitalInsurance: HospitalOrBasicInsurance, gender: GenderOrPrenatal): Item[] {
  const items = [
    mapHospitalVivaVariantItemOrUndefined(hospitalInsurance.general?.price, 'HOSPITAL_VIVA_GENERAL', gender),
    mapHospitalVivaVariantItemOrUndefined(hospitalInsurance.flex.flex24?.price, 'HOSPITAL_VIVA_FLEX_24', gender),
    mapHospitalVivaVariantItemOrUndefined(hospitalInsurance.flex.flex48?.price, 'HOSPITAL_VIVA_FLEX_48', gender),
    mapHospitalVivaVariantItemOrUndefined(
      Math.min(...hospitalInsurance.semiPrivate.map((i) => i.price)),
      'HOSPITAL_VIVA_SEMI_PRIVATE',
      gender,
    ),
    mapHospitalVivaVariantItemOrUndefined(
      Math.min(...hospitalInsurance.private.map((i) => i.price)),
      'HOSPITAL_VIVA_PRIVATE',
      gender,
    ),
  ];
  return items.filter((i) => isNotNullOrUndefined(i)).map((i, index) => ({ ...i, index: index + 1 })) as Item[];
}

export function mapBasicSupplementaryInsuranceDetailItem(
  offerType: BasicOfferType,
  gender: GenderOrPrenatal,
  price: number,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  return mapDetailItem(
    basicId[offerType],
    supplementaryInsuranceNames['basic'],
    price,
    gender,
    totalPersons,
    contractDuration,
    existingCustomer,
    insurer,
  );
}

export function mapOutpatientSupplementaryInsuranceDetailItem(
  level: Level,
  gender: GenderOrPrenatal,
  price: number,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  return mapDetailItem(
    outpatientId[level],
    supplementaryInsuranceNames['outpatient'],
    price,
    gender,
    totalPersons,
    contractDuration,
    existingCustomer,
    insurer,
  );
}

export function mapOutpatientVivaSupplementaryInsuranceDetailItem(
  level: Level,
  gender: GenderOrPrenatal,
  price: number,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  return mapDetailItem(
    outpatientVivaId[level],
    supplementaryInsuranceNames['outpatientViva'],
    price,
    gender,
    totalPersons,
    contractDuration,
    existingCustomer,
    insurer,
  );
}

export function mapComplementarySupplementaryInsuranceDetailItem(
  level: Level,
  gender: GenderOrPrenatal,
  price: number,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  return mapDetailItem(
    complementaryId[level],
    supplementaryInsuranceNames['complementary'],
    price,
    gender,
    totalPersons,
    contractDuration,
    existingCustomer,
    insurer,
  );
}

export function mapYouthSupplementaryInsuranceDetailItem(
  level: Level,
  gender: GenderOrPrenatal,
  price: number,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  return mapDetailItem(
    youthId[level],
    supplementaryInsuranceNames['youth'],
    price,
    gender,
    totalPersons,
    contractDuration,
    existingCustomer,
    insurer,
  );
}

export function mapHospitalSupplementaryInsuranceDetailItem(
  offerType: HospitalOfferType,
  gender: GenderOrPrenatal,
  price: number,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  return mapDetailItem(
    hospitalId[offerType],
    supplementaryInsuranceNames['hospital'],
    price,
    gender,
    totalPersons,
    contractDuration,
    existingCustomer,
    insurer,
  );
}

export function mapHospitalVivaSupplementaryInsuranceDetailItem(
  offerType: HospitalVivaOfferType,
  gender: GenderOrPrenatal,
  price: number,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  return mapDetailItem(
    hospitalVivaId[offerType],
    supplementaryInsuranceNames['hospitalViva'],
    price,
    gender,
    totalPersons,
    contractDuration,
    existingCustomer,
    insurer,
  );
}

export function mapSecondarySupplementaryInsuranceDetailItem(
  supplementaryInsuranceType: SupplementaryInsuranceTypes,
  gender: GenderOrPrenatal,
  price: number,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  const insuranceName = supplementaryInsuranceNames[supplementaryInsuranceType];
  return mapDetailItem(
    supplementaryInsuranceIds[supplementaryInsuranceType],
    insuranceName,
    price,
    gender,
    totalPersons,
    contractDuration,
    existingCustomer,
    insurer,
  );
}

function mapDetailItem(
  itemId: string,
  itemName: string,
  price: number,
  gender: GenderOrPrenatal,
  totalPersons?: number,
  contractDuration?: ContractDuration,
  existingCustomer?: boolean,
  insurer?: Insurer,
): Item {
  return {
    item_id: itemId,
    item_name: itemName,
    item_category: CATEGORY.SUPPLEMENTARY_INSURANCE,
    item_category3: genderNames[gender],
    item_category4: totalPersons !== undefined ? mapCustomerType(totalPersons) : undefined,
    item_category5: existingCustomer !== undefined ? mapExistingCustomer(existingCustomer) : undefined,
    item_brand: insurer !== undefined ? mapBrand(insurer) : undefined,
    price: price,
    quantity: 1,
    contract_length:
      contractDuration && existingCustomer !== undefined ? mapContractLength(contractDuration) : undefined,
  };
}

export function mapInsuranceIdForKey(key: SupplementaryInsuranceTypes): string {
  return supplementaryInsuranceIds[key];
}
